import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SharedModule } from 'src/app/demo/shared/shared.module';
import { ThemeLayoutService } from 'src/app/@theme/services/theme-layout.service';
import { Subject, takeUntil } from 'rxjs';
import { RTL } from 'src/app/@theme/const';

export interface PageEvent {
  page: number;
  pageSize: number;
  length: number;
}

@Component({
  selector: 'app-custom-pagination',
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: './custom-pagination.component.html',
  styleUrl: './custom-pagination.component.scss'
})
export class CustomPaginationComponent implements OnChanges, OnInit {
  pageSize: number = 100;
  currentPage: number = 1;
  @Input({ required: true }) totalRecords: number = 0;
  alignment: 'left' | 'right' = 'left';

  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() pageSizeChange = new EventEmitter<number>();

  totalPages: number = 1;
  startRecord: number = 1;
  endRecord: number = 100;
  recordRange: string = '';

  private destroy$: Subject<void> = new Subject<void>();

  constructor(
    private themeLayoutService: ThemeLayoutService
  ) {
    this.themeLayoutService.directionChange.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.updateRecordRange();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.emitPageChange();
  }

  ngOnInit(): void {
    this.calculatePagination();
    this.updateRecordRange();
  }

  private calculatePagination(): void {
    this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
    this.startRecord = (this.currentPage - 1) * this.pageSize + 1;
    this.endRecord = Math.min(this.currentPage * this.pageSize, this.totalRecords);
  }

  goToFirstPage(): void {
    if (this.currentPage > 1) {
      this.currentPage = 1;
      this.emitPageChange();
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.emitPageChange();
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.emitPageChange();
    }
  }

  goToLastPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage = this.totalPages;
      this.emitPageChange();
    }
  }

  goToCertainPage(page: number): void {
    if (!page || this.totalRecords <= 100 || this.totalPages === 0) {
      this.currentPage = 1;
      return;
    }
    else if (page > this.totalPages) {
      this.currentPage = this.totalPages;
      this.emitPageChange();
      return;
    }
    else {
      this.currentPage = page;
      this.emitPageChange();
    }
  }

  allowOnlyNumbers(event: KeyboardEvent) {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  private emitPageChange(): void {
    this.calculatePagination();
    this.updateRecordRange();
    this.pageChange.emit({
      page: this.currentPage,
      pageSize: this.pageSize,
      length: this.totalRecords
    });
  }

  get isFirstPage(): boolean {
    return this.currentPage === 1;
  }

  get isLastPage(): boolean {
    return this.currentPage === this.totalPages;
  }

  updateRecordRange(): void {
    this.recordRange = `${this.startRecord} - ${this.endRecord}`
  }

  get formattedTotalRecords(): string {
    return new Intl.NumberFormat().format(this.totalRecords);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
